import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO, { firmJsonLd } from "@/components/SEO";
import { Eyebrow, PrimaryCTA, SectionNum } from "@/components/ui-bits";
import { FIRM } from "@/data/firm";
import { VISIBLE_PRACTICE_AREAS } from "@/data/practiceAreas";
import { ATTORNEYS } from "@/data/attorneys";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    num: "01",
    title: "Deliberate scope",
    body:
      "The firm's practice areas were chosen for their intersection — business, intellectual property, and the federal regulatory regimes that increasingly govern them. A client rarely faces a corporate question that stops at the corporate line.",
  },
  {
    num: "02",
    title: "Senior on the file",
    body:
      "The attorney of record is the attorney on the file. There are no committees between the client and counsel, and no forced staffing on unrelated work. Each partner runs their own matters.",
  },
  {
    num: "03",
    title: "Speed without loss of care",
    body:
      "Because the practice is intentionally focused, matters move. Clients hear back the same day. Drafts arrive when they were promised. The firm is built to be the fastest thoughtful counsel a client works with — not the largest.",
  },
];

export default function About() {
  return (
    <Layout>
      <SEO
        title="About the Firm"
        description={`${FIRM.longName} advises closely-held companies, founders, and international clients on business transactions, intellectual property, and the federal regulatory practices that increasingly bear on them.`}
        path="/about"
        jsonLd={firmJsonLd()}
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <Eyebrow>The Firm</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-5xl">
            A modern firm for the business and regulatory matters that actually reach the front page.
          </h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            {FIRM.longName} advises closely-held companies, founders, and international clients on corporate transactions, intellectual property, and the federal regulatory practices — international trade, export compliance, CFIUS, government contracts, the semiconductor industry, FCC, and insurance — that increasingly bear on them.
          </p>
        </div>
      </section>

      {/* Vision / origin */}
      <section className="border-b border-hairline bg-ivory" data-testid="about-vision">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Origin</Eyebrow>
            <SectionNum className="mt-1">01</SectionNum>
          </div>
          <div className="lg:col-span-8 prose-law max-w-3xl">
            <p>
              Shelton Law was founded after a career representing companies in the maritime, defense, and commercial space industries — matters that consistently landed at the intersection of business and federal regulation. The firm was organized around that intersection on purpose. Its practice areas were chosen, and its structure was chosen, for the attorneys inside it.
            </p>
            <p>
              The intent is straightforward: build a firm where the attorney of record is genuinely the attorney on the file — with the sophistication to handle serious cross-border and federal regulatory work, and the discipline to move faster than the size of the matter would ordinarily allow.
            </p>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="border-b border-hairline bg-ivory-alt" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <Eyebrow>How the Firm Works</Eyebrow>
              <SectionNum className="mt-1">02</SectionNum>
              <h2 className="display-2 text-ink mt-5 leading-tight">Three commitments.</h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-ink-soft text-[1.0625rem] leading-relaxed">
                Three commitments that together define what it is like to work with the firm.
              </p>
            </div>
          </div>

          <ul className="border-t border-hairline" data-testid="about-pillars-list">
            {PILLARS.map((p) => (
              <li key={p.num} className="border-b border-hairline grid grid-cols-12 gap-6 py-10 lg:py-12 items-start">
                <SectionNum className="col-span-2 sm:col-span-1 self-start pt-2">{p.num}</SectionNum>
                <div className="col-span-10 sm:col-span-4">
                  <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">{p.title}</h3>
                </div>
                <div className="col-span-12 sm:col-span-7 text-ink-soft text-[1.0625rem] leading-relaxed">
                  {p.body}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Practice areas summary */}
      <section className="border-b border-hairline" data-testid="about-practice-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>The Practice</Eyebrow>
            <SectionNum className="mt-1">03</SectionNum>
            <h2 className="display-2 text-ink mt-5 leading-tight">Nine practice areas.</h2>
            <div className="mt-8">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold"
                data-testid="about-practice-cta"
              >
                Full Practice Areas <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ul className="border-t border-hairline">
              {VISIBLE_PRACTICE_AREAS.map((p) => (
                <li key={p.slug} className="border-b border-hairline grid grid-cols-12 gap-4 py-5">
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-1.5">{p.number}</SectionNum>
                  <div className="col-span-10 sm:col-span-11">
                    <Link
                      to={`/services/${p.slug}`}
                      className="font-serif text-xl text-ink leading-tight hover:text-brass transition-colors"
                      data-testid={`about-practice-${p.slug}`}
                    >
                      {p.name}
                    </Link>
                    <p className="mt-1 text-ink-soft text-sm leading-relaxed">{p.kicker}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* People / founder pointer */}
      <section className="bg-ivory border-b border-hairline" data-testid="about-people-pointer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Eyebrow>The Attorneys</Eyebrow>
            <SectionNum className="mt-1">04</SectionNum>
            <h2 className="display-2 text-ink mt-5 leading-tight max-w-2xl">
              {ATTORNEYS.length === 1
                ? "Founded by Jack Shelton."
                : `${ATTORNEYS.length} attorneys of record.`}
            </h2>
            <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
              Individual attorney biographies, admissions, and speaking engagements are on the People page.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <PrimaryCTA to="/people" testId="about-people-cta">See the People</PrimaryCTA>
          </div>
        </div>
      </section>

      {/* Careers hook temporarily hidden per firm direction. */}

      {/* Contact CTA */}
      <section className="bg-ink text-ivory" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-6">Engagement Inquiries</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
              Initial consultations are arranged by email.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right space-y-3">
            <a href={FIRM.emailHref} className="block font-serif text-2xl lg:text-3xl text-ivory hover:text-brass-light transition-colors" data-testid="about-cta-email">
              {FIRM.email}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
